import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Trash2, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api, useAuth, formatApiError } from "@/auth/AuthContext";
import { useCart, fmt } from "@/journeys/CartContext";
import { CurrencyToggle } from "@/pages/JourneysShop";
import { useLang } from "@/contexts/LangContext";
import { jx } from "@/i18n/journeyLabels";

const useQuotes = (items, currency) => {
  const [quotes, setQuotes] = useState({});
  useEffect(() => {
    let ok = true;
    (async () => {
      const out = {};
      for (const it of items) {
        try {
          const { data } = await api.post(`/journeys/quote?currency=${currency}`, it);
          out[it.key] = { ...data.quote };
        } catch (e) { out[it.key] = { error: formatApiError(e, "Pricing unavailable") }; }
      }
      if (ok) setQuotes(out);
    })();
    return () => { ok = false; };
  }, [items, currency]);
  return quotes;
};

export function CartPage() {
  const { items, removeItem, currency } = useCart();
  const { lang } = useLang();
  const L = jx(lang);
  const quotes = useQuotes(items, currency);
  const total = Object.values(quotes).reduce((s, q) => s + (q?.total || 0), 0);
  return (
    <div className="min-h-screen bg-[#F7F5F0] font-body text-[#121212]">
      <SEO title="Your Journey Cart — The China Explorer" description="Review your selected China journeys." path="/journeys/cart" />
      <Header />
      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-32 pb-20">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h1 className="font-heading text-4xl text-[#0E2A5C]">{L.cartTitle}</h1>
          <CurrencyToggle />
        </div>
        {items.length === 0 ? (
          <div className="mt-10 bg-white border border-[#EAE6DF] p-12 text-center" data-testid="cart-empty">
            <p className="text-[#4A4A4A]">{L.cartEmpty}</p>
            <Link to="/journeys" className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#C62828]">{L.exploreJourneys} <ArrowRight size={13} /></Link>
          </div>
        ) : (
          <div className="mt-8 space-y-4" data-testid="cart-items">
            {items.map((it) => {
              const q = quotes[it.key];
              return (
                <div key={it.key} className="bg-white border border-[#EAE6DF] p-5 flex gap-5 items-center" data-testid={`cart-item-${it.slug}`}>
                  <img src={it.hero_image} alt="" className="h-20 w-28 object-cover hidden sm:block" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg text-[#0E2A5C]">{it.title}</h3>
                    <p className="text-xs text-[#4A4A4A] mt-1">
                      {it.departure_label || it.preferred_date || L.dateTBC} · {it.adults} {L.adultsShort}{it.children ? `, ${it.children} ${L.childrenShort}` : ""} · {it.room_type} {L.roomShort}{it.addons.length ? ` · +${it.addons.length}` : ""}
                    </p>
                    <div className="mt-2 flex gap-4 text-[11px] uppercase tracking-[0.15em] font-semibold">
                      <Link to={`/journeys/${it.slug}`} className="text-[#0E2A5C] hover:text-[#C62828]">{L.edit}</Link>
                      <button onClick={() => removeItem(it.key)} data-testid={`remove-${it.slug}`} className="text-[#C62828] inline-flex items-center gap-1"><Trash2 size={11} /> {L.remove}</button>
                    </div>
                  </div>
                  <p className="font-heading text-xl text-[#0E2A5C]">{q?.error ? <span className="text-xs text-[#C62828]">{q.error}</span> : q ? fmt(q.total, currency) : "…"}</p>
                </div>
              );
            })}
            <div className="bg-white border border-[#EAE6DF] p-6 flex items-center justify-between">
              <Link to="/journeys" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] hover:text-[#0E2A5C]">{L.continueShopping}</Link>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#4A4A4A]">{L.grandTotal}</p>
                <p className="font-heading text-3xl text-[#C62828]" data-testid="cart-total">{fmt(total, currency)}</p>
              </div>
            </div>
            <Link to="/journeys/checkout" data-testid="checkout-button"
              className="block w-full text-center py-4 bg-[#C62828] hover:bg-[#B71C1C] text-white text-xs uppercase tracking-[0.2em] font-semibold transition-colors">
              {L.checkout} <ArrowRight size={14} className="inline" />
            </Link>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const Input = ({ label, k, form, setForm, type = "text", required }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A4A4A]">{label}{required && " *"}</label>
    <input type={type} required={required} value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
      data-testid={`co-${k}`} className="mt-1 w-full px-3.5 py-3 bg-white border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm" />
  </div>
);

export function JourneyCheckout() {
  const { items, currency, clear } = useCart();
  const { user } = useAuth();
  const { lang } = useLang();
  const L = jx(lang);
  const quotes = useQuotes(items, currency);
  const total = Object.values(quotes).reduce((s, q) => s + (q?.total || 0), 0);
  const [form, setForm] = useState({});
  const [createAccount, setCreateAccount] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const { data } = await api.post("/journeys/checkout", {
        items: items.map(({ key, title, hero_image, duration_label, departure_label, ...rest }) => rest),
        currency, origin_url: window.location.origin,
        customer: { full_name: form.full_name, email: form.email, phone: form.phone, whatsapp: form.whatsapp || form.phone, nationality: form.nationality || "", residence: form.residence || "" },
        traveller_details: (form.travellers || "").split("\n").filter(Boolean).map((n) => ({ name: n.trim() })),
        create_account: createAccount && !user, account_password: form.password,
      });
      window.location.href = data.url;
    } catch (err) { setError(formatApiError(err)); setLoading(false); }
  };

  if (items.length === 0) return <CartPage />;
  return (
    <div className="min-h-screen bg-[#F7F5F0] font-body text-[#121212]">
      <SEO title="Checkout — The China Explorer" description="Secure checkout." path="/journeys/checkout" />
      <Header />
      <div className="max-w-3xl mx-auto px-6 md:px-12 pt-32 pb-20">
        <h1 className="font-heading text-4xl text-[#0E2A5C]">{L.checkout}</h1>
        {params.get("payment") === "cancelled" && <p className="mt-3 text-sm text-[#C62828] bg-white border-l-2 border-[#C62828] p-3" data-testid="payment-cancelled">{L.coCancelled}</p>}
        <form onSubmit={submit} className="mt-8 bg-white border border-[#EAE6DF] p-6 md:p-8" data-testid="checkout-form">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C62828] font-semibold mb-4">✦ {L.coCustomerInfo}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label={L.coFullName} k="full_name" form={form} setForm={setForm} required />
            <Input label={L.coEmail} k="email" type="email" form={form} setForm={setForm} required />
            <Input label={L.coPhone} k="phone" form={form} setForm={setForm} required />
            <Input label={L.coWhatsapp} k="whatsapp" form={form} setForm={setForm} />
            <Input label={L.coNationality} k="nationality" form={form} setForm={setForm} />
            <Input label={L.coResidence} k="residence" form={form} setForm={setForm} />
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C62828] font-semibold mt-8 mb-2">✦ {L.coTravellers}</p>
          <textarea rows={3} placeholder={L.coTravellersPh} value={form.travellers || ""}
            onChange={(e) => setForm((f) => ({ ...f, travellers: e.target.value }))} data-testid="co-travellers"
            className="w-full px-3.5 py-3 border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm" />
          <p className="text-[11px] text-[#4A4A4A] mt-1">{L.coPassportNote}</p>
          {!user && (
            <div className="mt-6 border-t border-[#EAE6DF] pt-5">
              <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" checked={createAccount} onChange={(e) => setCreateAccount(e.target.checked)} data-testid="co-create-account" className="h-4 w-4 accent-[#C62828]" />
                {L.coCreateAccount}
              </label>
              {createAccount && <div className="mt-3 max-w-xs"><Input label={L.coPassword} k="password" type="password" form={form} setForm={setForm} required /></div>}
            </div>
          )}
          <div className="mt-8 pt-5 border-t border-[#EAE6DF] flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0E2A5C]">{L.total} ({items.length})</span>
            <span className="font-heading text-3xl text-[#C62828]" data-testid="checkout-total">{fmt(total, currency)}</span>
          </div>
          {error && <p className="mt-3 text-sm text-[#C62828]" data-testid="checkout-error">{error}</p>}
          <button type="submit" disabled={loading || total <= 0} data-testid="pay-button"
            className="mt-5 w-full h-[54px] bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-50 text-white text-xs uppercase tracking-[0.2em] font-semibold inline-flex items-center justify-center gap-2 transition-colors">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <>Continue to Secure Payment <ArrowRight size={14} /></>}
          </button>
          <p className="mt-3 text-center text-[11px] text-[#4A4A4A]">Card · Apple Pay · Google Pay — processed securely by Stripe</p>
        </form>
      </div>
      <Footer />
    </div>
  );
}

export function BookingResult() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user, startSession } = useAuth();
  const { clear } = useCart();
  const { lang } = useLang();
  const L = jx(lang);
  const [state, setState] = useState({ status: "checking" });
  const [claim, setClaim] = useState({ password: "", loading: false, error: null, done: false });
  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/journeys/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { clear(); setState({ status: "paid", bookings: data.bookings }); return; }
        if (data.status === "expired") { setState({ status: "failed" }); return; }
      } catch { /* retry */ }
      if (++tries < 8) setTimeout(poll, 2500); else setState({ status: "timeout" });
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F7F5F0] font-body text-[#121212]">
      <SEO title="Booking Status — The China Explorer" description="Booking confirmation." path="/journeys/booking" />
      <Header />
      <div className="max-w-2xl mx-auto px-6 pt-36 pb-20 text-center">
        {state.status === "checking" && <><Loader2 className="mx-auto animate-spin text-[#C62828]" size={32} /><p className="mt-4 text-[#4A4A4A]">{L.brChecking}</p></>}
        {state.status === "paid" && (
          <div className="bg-white border border-[#EAE6DF] p-10" data-testid="booking-confirmed">
            <CheckCircle2 className="mx-auto text-emerald-600" size={40} />
            <h1 className="font-heading text-4xl text-[#0E2A5C] mt-4">{L.brPaidTitle}</h1>
            {state.bookings.map((b) => (
              <p key={b.id} className="mt-3 text-sm text-[#4A4A4A]"><strong className="text-[#0E2A5C]" data-testid="booking-reference">{b.reference}</strong> — {b.title}</p>
            ))}
            <p className="mt-4 text-sm text-[#4A4A4A]">{L.brEmail}</p>
            {user ? (
              <Link to="/my-journeys" data-testid="goto-my-journeys" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#0E2A5C] border border-[#0E2A5C] px-6 py-3 hover:bg-[#0E2A5C] hover:text-white transition-colors">{L.brMyJourneys} <ArrowRight size={13} /></Link>
            ) : claim.done ? (
              <div className="mt-6 bg-emerald-50 border border-emerald-200 p-5" data-testid="claim-success">
                <p className="text-sm text-emerald-800 font-semibold">{L.brReady}</p>
                <Link to="/my-journeys" data-testid="goto-my-journeys" className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#C62828]">{L.brViewMy} <ArrowRight size={13} /></Link>
              </div>
            ) : (
              <div className="mt-8 border-t border-[#EAE6DF] pt-6 text-left" data-testid="claim-account">
                <p className="text-xs uppercase tracking-[0.25em] text-[#C62828] font-semibold">✦ {L.brCreate}</p>
                <p className="mt-2 text-sm text-[#4A4A4A]">{L.brCreatePitch}</p>
                <form className="mt-4 flex flex-col sm:flex-row gap-3" onSubmit={async (e) => {
                  e.preventDefault();
                  setClaim((c) => ({ ...c, loading: true, error: null }));
                  try {
                    const { data } = await api.post("/journeys/account/claim", { session_id: sessionId, password: claim.password });
                    startSession(data.token, data.user);
                    setClaim((c) => ({ ...c, loading: false, done: true }));
                  } catch (err) { setClaim((c) => ({ ...c, loading: false, error: formatApiError(err) })); }
                }}>
                  <input type="password" required minLength={8} placeholder={L.coPassword} value={claim.password}
                    onChange={(e) => setClaim((c) => ({ ...c, password: e.target.value }))} data-testid="claim-password"
                    className="flex-1 px-3.5 py-3 border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm" />
                  <button type="submit" disabled={claim.loading} data-testid="claim-submit"
                    className="px-6 py-3 bg-[#0E2A5C] text-white text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-60">
                    {claim.loading ? L.brCreating : L.brCreateBtn}
                  </button>
                </form>
                {claim.error && <p className="mt-2 text-sm text-[#C62828]" data-testid="claim-error">{claim.error} {claim.error.includes("already exists") && <Link to="/login?next=/my-journeys" className="underline font-semibold">Sign in</Link>}</p>}
              </div>
            )}
            <Link to="/journeys" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#C62828]">{L.brExplore} <ArrowRight size={13} /></Link>
          </div>
        )}
        {["failed", "timeout", "error"].includes(state.status) && (
          <div className="bg-white border border-[#EAE6DF] p-10" data-testid="booking-pending">
            <h1 className="font-heading text-3xl text-[#0E2A5C]">{L.brPendingTitle}</h1>
            <p className="mt-3 text-sm text-[#4A4A4A]">{L.brPendingMsg}</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
